import Decimal from 'decimal.js';

import { HelpTextVariant } from '@/components/form/baseInputs/inputTypes';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import {
  DollarPercentPattern,
  DollarPercentValues,
} from './DollarPercentButtonGroup';

export function getTransferSourceValueHelpText(
  sourceValue: Decimal,
  isExceeding = false
): { helpText: string; helpTextVariant: HelpTextVariant | undefined } {
  const label = isExceeding ? 'Exceeds current net value' : 'Current net value';

  return {
    helpText: `${label}: ${formatCurrencyNoDecimals(sourceValue)}. Does not include future transfers into or out of this entity.`,
    helpTextVariant: isExceeding ? 'warning' : undefined,
  };
}

interface AssetOptionDetails {
  totalValue: Decimal;
}

interface SelectedValue {
  amount: Decimal | null;
  percent: Decimal | null;
}

export function calculateTotalValue({
  selectedAssetIds,
  selectedAssetClassAndBusinessValues,
  assetOptionDetails,
  pattern,
}: {
  selectedAssetIds: string[] | null;
  selectedAssetClassAndBusinessValues: Record<string, SelectedValue> | null;
  assetOptionDetails: Record<string, AssetOptionDetails>;
  pattern: DollarPercentPattern;
}): Decimal {
  if (!selectedAssetIds || !selectedAssetClassAndBusinessValues) {
    return new Decimal(0);
  }

  return selectedAssetIds.reduce((sum, assetId) => {
    const details = assetOptionDetails[assetId];
    const selectedValue = selectedAssetClassAndBusinessValues[assetId];
    if (!details || !selectedValue) {
      return sum;
    }

    if (pattern === DollarPercentValues.DOLLAR) {
      return sum.plus(selectedValue.amount ?? new Decimal(0));
    } else {
      // For percentages, calculate the dollar value based on the percentage of the total asset value
      const percentage = new Decimal(selectedValue.percent ?? 0).dividedBy(100);
      return sum.plus(details.totalValue.times(percentage));
    }
  }, new Decimal(0));
}
